import React from 'react';
import { Modal, Typography, Divider, Tag, Alert, Steps, Collapse, List, Space, Tabs } from 'antd';
import { 
  QuestionCircleOutlined, 
  RocketOutlined, 
  ThunderboltOutlined,
  RobotOutlined,
  PhoneOutlined,
  UserOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  MessageOutlined,
  BookOutlined,
  ForkOutlined,
  NodeIndexOutlined,
  BuildOutlined,
  InteractionOutlined,
  NotificationOutlined,
  SafetyOutlined,
  BulbOutlined
} from '@ant-design/icons';
import BotManager from './BotManager';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const UserManual = ({ visible, onClose, templates, functions, loading, onUpdate, account }) => {
  const manualItems = [
    {
      key: 'bot-factory',
      label: (
        <span>
          <BulbOutlined />
          AI Bot Factory
        </span>
      ),
      children: (
        <div style={{ padding: '0 24px' }}>
          <BotManager 
            templates={templates} 
            functions={functions} 
            loading={loading} 
            onUpdate={onUpdate} 
            account={account}
          />
        </div>
      ),
    },
    {
      key: 'guide',
      label: (
        <span>
          <BookOutlined />
          Platform Guide
        </span>
      ),
      children: (
        <div style={{ padding: '0 24px' }}>
          <Alert
            message="Welcome to the Multi-Agent WhatsApp Ecosystem!"
            description="This professional guide details every feature of your power-packed WhatsApp Bot Management System. Use the navigation below to explore."
            type="info"
            showIcon
            style={{ marginBottom: 24, borderRadius: '12px', border: '1px solid #bae7ff' }}
          />

          <Collapse defaultActiveKey={['1']} ghost expandIconPosition="end" className="manual-collapse">
            {/* 1. Dashboard & Getting Started */}
            <Panel header={<strong><RocketOutlined /> 1. Getting Started & Dashboard</strong>} key="1">
              <Title level={4}>The Platform Overview</Title>
              <Paragraph>
                The Master Agent System is a multi-tenant, modular platform designed to manage and scale AI-driven WhatsApp interactions.
              </Paragraph>
              <List
                size="small"
                bordered
                dataSource={[
                  'Unified Dashboard: Manage multiple WABA (WhatsApp Business API) accounts.',
                  'Modular Architecture: Scale agents and routing rules independently.',
                  'Multi-Tenant Support: Securely manage different business units or silos.'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Divider />
              <Title level={5}>Core Design</Title>
              <Space wrap>
                <Tag color="geekblue">Meta Cloud API</Tag>
                <Tag color="purple">Intelligent LLM Routing</Tag>
                <Tag color="cyan">Multi-Agent Orchestration</Tag>
                <Tag color="green">Premium UI/UX</Tag>
              </Space>
            </Panel>

            {/* 2. Account & Connectivity */}
            <Panel header={<strong><SettingOutlined /> 2. Connectivity & Account Setup</strong>} key="2">
              <Title level={4}>Meta Cloud API Integration</Title>
              <Paragraph>
                Connect your official WhatsApp Business Accounts (WABA) using our self-onboarding portal.
              </Paragraph>
              <Steps
                direction="vertical"
                size="small"
                items={[
                  {
                    title: 'Business Setup',
                    description: 'Enter your WABA ID, Phone Number ID, and System User Token in "API Keys & Account".'
                  },
                  {
                    title: 'Webhook Configuration',
                    description: 'Configure your Meta developer dashboard with the provided Callback URL and Verify Token.'
                  },
                  {
                    title: 'Number Management',
                    description: 'Sync and manage multiple active phone numbers per account.'
                  },
                  {
                    title: 'Product Catalog',
                    description: 'Manage your product inventory for automated sales bots.'
                  }
                ]}
              />
            </Panel>

            {/* 3. Inbound Routing */}
            <Panel header={<strong><ForkOutlined /> 3. Intelligent Inbound Routing</strong>} key="3">
              <Title level={4}>Rule-Based Traffic Management</Title>
              <Paragraph>
                Decide exactly how incoming messages are handled before they even reach an agent.
              </Paragraph>
              <List
                size="small"
                bordered
                dataSource={[
                  'Inbound Routing: Define global rules for new conversations based on source, keywords, or status.',
                  'User Mappings: Assign specific users (by phone) to specialized agent templates.',
                  'Group Mappings: Route group chats to specific community manager bots.',
                  'Number Mappings: Legacy support for mapping different phone numbers to specific agents.'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Divider />
              <Alert 
                type="success" 
                message="Smart Fallback" 
                description="If no specific rule matches, the Master Bot Config takes over with its 'Default Agent'." 
              />
            </Panel>

            {/* 4. Agent Factory */}
            <Panel header={<strong><BuildOutlined /> 4. Agent Factory (Bot Creator)</strong>} key="4">
              <Title level={4}>Creating High-Performance AI Bots</Title>
              <Paragraph>
                The Bot Creator is an AI-powered assistant that helps you build specialized agents in seconds.
              </Paragraph>
              <Paragraph>
                <em>Looking to build a bot right now? Switch to the "AI Bot Factory" tab above!</em>
              </Paragraph>
              <Steps
                direction="vertical"
                size="small"
                items={[
                  {
                    title: 'Bot Purpose',
                    description: 'Describe the bot\'s goal. The AI will suggest a system prompt and configuration.'
                  },
                  {
                    title: 'Agent Templates',
                    description: 'Fine-tune personality, tone, model parameters (GPT-4o, etc.), and guardrails.'
                  },
                  {
                    title: 'Custom Functions',
                    description: 'Connect your bot to external APIs (Weather, CRM, SQL, JSON) via dynamic function calling.'
                  }
                ]}
              />
            </Panel>

            {/* 5. Human-AI Handover */}
            <Panel header={<strong><InteractionOutlined /> 5. Human-AI Collaboration (Handover)</strong>} key="5">
              <Title level={4}>The Best of Both Worlds</Title>
              <Paragraph>
                Seamlessly transition between automated AI and personalized human support.
              </Paragraph>
              <Collapse ghost>
                <Panel header="Manual Handover" key="5a">
                  <Paragraph>
                    The AI can automatically mute itself and notify a human agent when it detects a complex query or when specifically configured in Routing Rules.
                  </Paragraph>
                </Panel>
                <Panel header="AI Takeback (Auto-Resumption)" key="5b">
                  <Paragraph>
                    Never leave a customer hanging. If a human agent remains silent for a configurable duration (e.g., 30 mins), the AI automatically "takes back" control to keep the conversation moving.
                  </Paragraph>
                  <Tag color="gold">Configurable in Account Settings</Tag>
                </Panel>
                <Panel header="AI Muting" key="5c">
                  <Paragraph>
                    When a human is active, the AI enters "Muted" mode. It still listens and maintains context (internal memory) but does not send outgoing messages.
                  </Paragraph>
                </Panel>
              </Collapse>
            </Panel>

            {/* 6. Messaging Tools (Flows & Templates) */}
            <Panel header={<strong><NodeIndexOutlined /> 6. Messaging: Flows & Templates</strong>} key="6">
              <Title level={4}>Rich Interactive Engagement</Title>
              <Paragraph>
                Leverage official WhatsApp features for high conversion rates.
              </Paragraph>
              <List
                size="small"
                dataSource={[
                  { title: 'WhatsApp Templates', desc: 'Pre-approved message templates with buttons and variables for outbound communication.' },
                  { title: 'WhatsApp Flows', desc: 'Build interactive forms, booking flows, and surveys directly inside WhatsApp.' },
                  { title: 'Broadcasts', desc: 'Schedule and send high-volume template messages to specific user groups.' }
                ]}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta title={item.title} description={item.desc} />
                  </List.Item>
                )}
              />
            </Panel>

            {/* 7. Group Management */}
            <Panel header={<strong><TeamOutlined /> 7. Group Reply Rules</strong>} key="7">
              <Title level={4}>Managing Community Silence</Title>
              <Paragraph>
                Avoid bot spam in busy groups with intelligent reply triggers.
              </Paragraph>
              <List
                size="small"
                bordered
                dataSource={[
                  'Mention-Only Mode: Respond only when tagged (@bot).',
                  'Delay Mode: Wait X minutes before replying, giving humans a chance to answer first.',
                  'Operating Hours: Limit bot activity to specific times.',
                  'Keyword Triggers: Only engage when specific topics are mentioned.'
                ]}
                renderItem={item => <List.Item>• {item}</List.Item>}
              />
            </Panel>

            {/* 8. Monitoring & Testing */}
            <Panel header={<strong><MessageOutlined /> 8. Monitoring, History & Testing</strong>} key="8">
              <Title level={4}>Real-Time Insights</Title>
              <Paragraph>
                Track every single movement of your multi-agent ecosystem.
              </Paragraph>
              <List
                size="small"
                dataSource={[
                  { title: 'Chat History', desc: 'Unified view of all conversations across all numbers and agents.' },
                  { title: 'Live Logs', desc: 'Technical log stream showing LLM thoughts, API calls, and routing logic.' },
                  { title: 'Live Test', desc: 'Sandbox environment to test agent templates without needing a real phone.' }
                ]}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta title={item.title} description={item.desc} />
                  </List.Item>
                )}
              />
            </Panel>

            {/* 9. Safety & Guardrails */}
            <Panel header={<strong><SafetyOutlined /> 9. Safety & Guardrails</strong>} key="9">
              <Title level={4}>Ensuring Reliable AI Response</Title>
              <Paragraph>
                Maintain brand reputation and quality with built-in guardrails.
              </Paragraph>
              <List
                size="small"
                bordered
                dataSource={[
                  'System Guardrails: Force the AI to stay within specific boundaries.',
                  'API Security: Encrypted storage for all tokens and sensitive keys.',
                  'Rate Limiting: Built-in protection against message bursts.'
                ]}
                renderItem={item => <List.Item>• {item}</List.Item>}
              />
            </Panel>

            {/* 10. Troubleshooting */}
            <Panel header={<strong><QuestionCircleOutlined /> 10. Troubleshooting</strong>} key="10">
              <Collapse ghost>
                <Panel header="Bot not sending messages" key="t1">
                  <Paragraph>Check Meta Developer dashboard for 'Invalid Token' or 'Payment Method Required' errors. Verify connection status in Live Logs.</Paragraph>
                </Panel>
                <Panel header="Templates not syncing" key="t2">
                  <Paragraph>Ensure your WABA ID and Phone Number ID are correctly connected in Settings.</Paragraph>
                </Panel>
                <Panel header="Handoff not working" key="t3">
                  <Paragraph>Verify 'Handover Enabled' is toggled ON in Account Settings and your manual agents are logged in.</Paragraph>
                </Panel>
              </Collapse>
            </Panel>
          </Collapse>
          
          <Divider />
          
          <Alert
            message="Need Technical Support?"
            description={
              <Paragraph style={{ marginBottom: 0 }}>
                Contact our engineering team via the "Team Details" ticket system or refer to the developer-only <strong>ADVANCED_FUNCTIONS_GUIDE.md</strong> on the server.
              </Paragraph>
            }
            type="info"
            style={{ borderRadius: '12px' }}
          />
        </div>
      ),
    }
  ];

  return (
    <Modal
      title={
        <Space>
          <RocketOutlined style={{ color: '#6366f1' }} />
          <span>Interactive Onboarding & Platform Guide</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      maskClosable={true}
      footer={null}
      width={1000}
      style={{ top: 20 }}
      className="manual-modal"
      bodyStyle={{ maxHeight: '90vh', overflowY: 'auto', padding: '12px 0' }}
    >
      <Tabs 
        defaultActiveKey="bot-factory" 
        items={manualItems} 
        centered
        className="premium-tabs"
        size="large"
      />
    </Modal>
  );
};

export default UserManual;

